import { fieldMap } from "@/constants/dataMap";

const findFieldName = (key: string, type: "elements" | "streams") => {
  const mapType = fieldMap[type];
  return mapType[key].name;
}

/**
 * 数据验证模型定义
 */
interface Material {
  cas: string;
  name: string;
  molar_frac: number | string | null | undefined;
}

interface Params {
  f: number | string | null | undefined;
  p: number | string | null | undefined;
  f_unit: string;
  p_unit: string;
  t?: number | string | null;
  t_unit?: string;
  physical_method?: string;
  phase?: string;
  mode?: string;
}

interface ErrorInfo {
  msg: string;
  name: string;
  type: 'stream' | 'element';
  field: string;
  fieldName: string;
  eleTypeName?: string | null;
}


//验证进料流股
const verlidStrems = (data: any) => {
  const errors: ErrorInfo[] = [];
  // 过滤出所有“进料”类型的节点，只对这些节点进行特定验证
  const feedNodes = data.filter((item: any) => item.type === "进料");
  feedNodes.forEach((item: any) => {
    const nodeInfo = {
      name: item.name,
      type: item.type
    };


    const checkParam = (key: keyof Params): void => {
      const value = item.params[key];
      const fieldName = findFieldName(key, "streams");
      if (value === null || value === undefined || value === "") {
        errors.push({
          msg: `请填写${nodeInfo.type}流股-${nodeInfo.name} ${fieldName}`,
          name: nodeInfo.name,
          type: 'stream',
          field: key,
          fieldName: fieldName,
        });
      } else if (typeof value !== 'number') {
        errors.push({
          msg: `${nodeInfo.type}流股-${nodeInfo.name} ${fieldName}必须为数字`,
          name: nodeInfo.name,
          type: 'stream',
          field: fieldName,
          fieldName: fieldName,
        });
      }
    };

    checkParam("f");
    checkParam("p");
    checkParam("t");

    // 验证 materials 中的 molar_frac
    const checkMaterial = (material: Material): void => {
      if (material.molar_frac === null || material.molar_frac === undefined || material.molar_frac === "") {
        errors.push(
          {
            msg: `${nodeInfo.type}流股-${nodeInfo.name}物料组成中${material.name}的摩尔分率`,
            name: nodeInfo.name,
            type: 'stream',
            field: 'molar_frac',
            fieldName: '摩尔分率',
          }
        );
      } else if (typeof material.molar_frac !== 'number') {
        errors.push(
          {
            msg: `${nodeInfo.type}流股-${nodeInfo.name}物料组成中${material.name}的摩尔分率必须为数字`,
            name: nodeInfo.name,
            type: 'stream',
            field: 'molar_frac',
            fieldName: '摩尔分率',
          }
        );
      }
    };
    item.materials.forEach(checkMaterial);
  });

  // 3. 返回结果：有错误返回错误数组，无错误返回 true
  if (errors.length > 0) {
    return errors;
  }
  return 'ok';
}

//验证设备
const verlidElements = (data: any,streams?:any) => {
  let errors: ErrorInfo[] = [];
  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const params = item.params;
    if (!params) {
      errors.push({
        msg: `${item.name}参数未设置。`,
        name: item.name,
        type: 'element',
        field: 'params',
        fieldName: '参数',
        eleTypeName: '参数',
      });
      continue; // 如果 params 缺失，后续的验证都无法进行
    }
    if (item.type === 'DistilStrict') {
      const towerErrors: ErrorInfo[] | 'ok' = validTower(item);
      if (towerErrors !== 'ok') {
        errors.push(...towerErrors);
      }
    } else if (item.type === 'Pump') {
      const pumpErrors: ErrorInfo[] | 'ok' = validPump(item);
      if (pumpErrors !== 'ok') {
        errors.push(...pumpErrors);
      }
    } else if (item.type === 'Flash2') {
      const flash2Errors: ErrorInfo[] | 'ok' = validFlash2(item);
      if (flash2Errors !== 'ok') {
        errors.push(...flash2Errors);
      }
    }else if(item.type === 'ShellAndTubeExchanger' || item.type === 'MultiStreamExchanger'){
      const exchangerErrors: ErrorInfo[] | 'ok' = valideExchanger(item,streams);
      if (exchangerErrors !== 'ok') {
        errors.push(...exchangerErrors);
      }
    }
  }
  if (errors.length > 0) {
    return errors;
  } else {
    return 'ok';
  }
}

//验证塔参数
const validTower = (item: any) => {
  const params = item.params;
  let errors: ErrorInfo[] = [];
  // 检查 N
  if (typeof params.N !== 'number' || isNaN(params.N)) {
    errors.push({
      msg: `${item.name}塔板数错误。`,
      name: item.name,
      type: 'element',
      field: 'N',
      fieldName: '塔板数',
      eleTypeName: item.type,
    });
  }

  // 检查 Ns
  if (!Array.isArray(params.Ns)) {
    errors.push({
      msg: `${item.name}进料流股位置错误。`,
      name: item.name,
      type: 'element',
      field: 'Ns',
      fieldName: '进料流股位置',
      eleTypeName: item.type,
    });
  }

  // 检查 No
  if (!Array.isArray(params.No)) {
    errors.push({
      msg: `${item.name}出料流股位置错误。`,
      name: item.name,
      type: 'element',
      field: 'No',
      fieldName: '出料流股位置',
      eleTypeName: item.type,
    });
  }

  // 检查 P_D
  if (typeof params.P_D !== 'number' || isNaN(params.P_D)) {
    errors.push({
      msg: `${item.name}塔顶压力错误。`,
      name: item.name,
      type: 'element',
      field: 'P_D',
      fieldName: '塔顶压力',
      eleTypeName: item.type,
    });
  }

  // 检查 pressure_drop
  if (typeof params.pressure_drop !== 'number' || isNaN(params.pressure_drop)) {
    errors.push({
      msg: `${item.name}整塔压降错误。`,
      name: item.name,
      type: 'element',
      field: 'pressure_drop',
      fieldName: '整塔压降',
      eleTypeName: item.type,
    });
  }

  // 检查 D_col塔板数
  if (typeof params.D_col !== 'number' || isNaN(params.D_col)) {
    errors.push({
      msg: `${item.name}塔径错误。`,
      name: item.name,
      type: 'element',
      field: 'D_col',
      fieldName: '塔径',
      eleTypeName: item.type,
    });
  }

  // 检查 D_col塔板数
  if (typeof params.d_hole !== 'number' || isNaN(params.d_hole)) {
    errors.push({
      msg: `${item.name}塔板孔径错误。`,
      name: item.name,
      type: 'element',
      field: 'd_hole',
      fieldName: '塔板孔径',
      eleTypeName: item.type,
    });
  }



  //验证塔板效率eff
  if (!params.eff || !Array.isArray(params.eff) || params.eff.length === 0) {
    errors.push({
      msg: `${item.name}塔板效率错误。`,
      name: item.name,
      type: 'element',
      field: 'eff',
      fieldName: '塔板效率',
      eleTypeName: item.type,
    })
  }

   // --------------------------
  // 遍历验证 eff
  // --------------------------
  for (let i = 0; i < params.eff.length; i++) {
    const child = params.eff[i];
    const { start, end } = child || {};
    // 检查 start / end 是否存在且为有效数字
    if (typeof start !== 'number' || !isFinite(start) || typeof end !== 'number' || !isFinite(end)) {
      errors.push({
        msg: `${item.name}塔板效率错误。`,
        name: item.name,
        type: 'element',
        field: 'eff',
        fieldName: '塔板效率',
        eleTypeName: item.type,
      })
    }

    // --------------------------
    // start 必须小于 end
    // --------------------------
    if (start >= end) {
      errors.push({
        msg: `${item.name}塔板效率错误。`,
        name: item.name,
        type: 'element',
        field: 'eff',
        fieldName: '塔板效率',
        eleTypeName: item.type,
      })
    }

    // --------------------------
    // start 和 end 必须 > 0 且 ≤ N
    // --------------------------
    if (start <= 0 || end <= 0 || start > params.N || end > params.N) {
      errors.push({
        msg: `${item.name}塔板效率错误。`,
        name: item.name,
        type: 'element',
        field: 'eff',
        fieldName: '塔板效率',
        eleTypeName: item.type,
      })
    }

    // --------------------------
    // 第一项 start 必须 = 1
    // --------------------------
    if (i === 0 && start !== 1) {
      errors.push({
        msg: `${item.name}塔板效率错误。`,
        name: item.name,
        type: 'element',
        field: 'eff',
        fieldName: '塔板效率',
        eleTypeName: item.type,
      })
    }

    // --------------------------
    // 最后一项 end 必须 = N
    // --------------------------
    if (i === params.eff.length - 1 && end !== params.N) {
      errors.push({
        msg: `${item.name}塔板效率错误。`,
        name: item.name,
        type: 'element',
        field: 'eff',
        fieldName: '塔板效率',
        eleTypeName: item.type,
      })
    }

    // --------------------------
    // 从第二项开始，start = 上一项 end + 1
    // --------------------------
    if (i > 0) {
      const prevEnd = params.eff[i - 1].end;
      if (start !== prevEnd + 1) {
        errors.push({
          msg: `${item.name}塔板效率错误。`,
          name: item.name,
          type: 'element',
          field: 'eff',
          fieldName: '塔板效率',
          eleTypeName: item.type,
        })
      }
    }
  }

  // 需要 N 为有效数字，Ns 和 No 为有效数组
  if (typeof params.N === 'number' && isFinite(params.N) && params.N >= 1) {
    const N_limit = Math.floor(params.N);

    // 验证 Ns 数组
    if (Array.isArray(params.Ns)) {
      for (const n of params.Ns) {
        if (typeof n !== 'number' || n < 1 || n > N_limit) {
          errors.push({
            msg: `${item.name}进料流股位置错误`,
            name: item.name,
            type: 'element',
            field: 'Ns',
            fieldName: '进料流股位置',
            eleTypeName: item.type,
          });
        }
      }
    }

    // 验证 No 数组
    if (!Array.isArray(params.No)) {
      for (const n of params.No) {
        if (typeof n !== 'number' || n < 1 || n > N_limit) {
          errors.push({
            msg: `${item.name}出料流股位置错误`,
            name: item.name,
            type: 'element',
            field: 'No',
            fieldName: '出料流股位置',
            eleTypeName: item.type,
          });
        }
      }
    }
  } else if (!isNaN(params.N)) {
    // 如果 N 字段存在但类型错误，则这里会捕捉
    errors.push({
      msg: `${item.name}塔板数必须为数值。`,
      name: item.name,
      type: 'element',
      field: 'N',
      fieldName: '塔板数',
      eleTypeName: item.type,
    });
  }
  if (errors.length > 0) {
    return errors;
  } else {
    return 'ok';
  }
}

//验证泵参数
const validPump = (item: any) => {
  const params = item.params;
  let errors: ErrorInfo[] = [];
  // 检查 p 压力
  if (typeof params.p !== 'number' || isNaN(params.p)) {
    errors.push({
      msg: `${item.name}压力错误。`,
      name: item.name,
      type: 'element',
      field: 'p',
      fieldName: '压力',
      eleTypeName: 'pump',
    });
  }
  if (errors.length > 0) {
    return errors;
  } else {
    return 'ok';
  }
}

//验证闪蒸罐参数
const validFlash2 = (item: any) => {
  const params = item.params;
  let errors: ErrorInfo[] = [];
  // 检查 t 压力
  if (typeof params.t !== 'number' || isNaN(params.t)) {
    errors.push({
      msg: `${item.name}温度错误。`,
      name: item.name,
      type: 'element',
      field: 't',
      fieldName: '温度',
      eleTypeName: item.type,
    });
  }
  // 检查 p 压力
  if (typeof params.p !== 'number' || isNaN(params.p)) {
    errors.push({
      msg: `${item.name}压力错误。`,
      name: item.name,
      type: 'element',
      field: 'p',
      fieldName: '压力',
      eleTypeName: item.type,
    });
  }
  if (errors.length > 0) {
    return errors;
  } else {
    return 'ok';
  }
}

//判断数组元素是否全为null
const areAllNull = (arr: any) => {
  if (!Array.isArray(arr)) {
    return false;
  }
  return arr.every(element => element === null);
}

//验证换热器
const valideExchanger = (item: any,streams?:any) => 
{
  const { params, inputStreams, outputStreams } = item;
  let errors: ErrorInfo[] = [];

  if (inputStreams.length > 2 && outputStreams.length > 2) 
  {
    // 检查 hot_out_T 热流股出口温度
    if (areAllNull(params.hot_out_T)) {
      errors.push({
        msg: `${item.name}热流股出口温度错误。`,
        name: item.name,
        type: 'element',
        field: 'hot_out_T', 
        fieldName: '热流股出口温度',
        eleTypeName: item.type,
      });
    }
    // 检查 hot_out_P 热流股出口压力
    if (areAllNull(params.hot_out_P)) {
      errors.push({
        msg: `${item.name}热流股出口压力错误。`,
        name: item.name,
        type: 'element',
        field: 'hot_out_P',
        fieldName: '热流股出口压力',
        eleTypeName: item.type,
      });
    }

  } 
  else 
  {
    if(params.streamAttribute)
    {
      if(params.streamAttribute==="热流股")
      {
        // 检查 hot_out_T 热流出口温度
        if (typeof params.hot_out_T !== 'number' || isNaN(params.hot_out_T)) {
          errors.push({
            msg: `${item.name}热流出口温度错误。`,
            name: item.name,
            type: 'element',
            field: 'hot_out_T', 
            fieldName: '热流出口温度',
            eleTypeName: item.type,
          });
        }
        // 检查 hot_out_P 热流出口压力
        if (typeof params.hot_out_P !== 'number' || isNaN(params.hot_out_P)) {
          errors.push({
            msg: `${item.name}热流股出口压力错误。`,
            name: item.name,
            type: 'element',
            field: 'hot_out_P',
            fieldName: '热流出口压力',
            eleTypeName: item.type,
          });
        }
      }
      else if(params.streamAttribute==="冷流股")
      {
        // 检查 cold_out_T 冷流股出口温度
        if (typeof params.cold_out_T !== 'number' || isNaN(params.cold_out_T)) {
          errors.push({
            msg: `${item.name}冷流股出口温度错误。`,
            name: item.name,
            type: 'element',
            field: 'cold_out_T', 
            fieldName: '冷流出口温度',
            eleTypeName: item.type,
          });
        }
        // 检查 cold_out_P 冷流出口压力
        if (typeof params.cold_out_P !== 'number' || isNaN(params.cold_out_P)) {
          errors.push({
            msg: `${item.name}冷流出口压力错误。`,
            name: item.name,
            type: 'element',
            field: 'cold_out_P',
            fieldName: '冷流出口压力',
            eleTypeName: item.type,
          });
        }
      }
    }
    else
    {
      errors.push({
        msg: `${item.name}选择的冷热流股属性错误。`,
        name: item.name,
        type: 'element',
        field: 'streamAttribute',
        fieldName: '流股属性',
        eleTypeName: item.type,
      });
    }

    if ((!params.stream_type) || Object.keys(params.stream_type).length<1) 
    {
      errors.push({
        msg: `${item.name}连接的冷热流股属性错误。`,
        name: item.name,
        type: 'element',
        field: 'stream_type',
        fieldName: '流股类型',
        eleTypeName: item.type,
      });
    }
  }

  //检查对应流股的温度
  if(item.type==="MultiStreamExchanger")
  {
    let inHotStreams = inputStreams.filter(item=>item.streamAttribute==="热流股");
    let outHotStreams = outputStreams.filter(item=>item.streamAttribute==="热流股");

    if(inHotStreams.length>0 && outHotStreams.length>0)
    {
      for(let i=0;i<outHotStreams.length;i++)
      {
        let outHotStreamName = outHotStreams[i].name;
        let streamRelationship = params["streamRelationship"].find(item=>item["出口流股"]===outHotStreamName);
        if(streamRelationship)
        {
          let inHotStreamName = streamRelationship["入口流股"];

          let outHotStream = streams.find(item=>item.name===outHotStreamName);
          let inHotStream = streams.find(item=>item.name===inHotStreamName);

          if(outHotStream && inHotStream)
          {
            if(outHotStream.params && inHotStream.params)
            {
              let outHotStreamT = outHotStream.params.t;
              let outHotStreamTUnit = outHotStream.params.t_unit;
              let inHotStreamT = inHotStream.params.t;
              let inHotStreamTUnit = inHotStream.params.t_unit;

              if(outHotStreamT && outHotStreamTUnit && inHotStreamT && inHotStreamTUnit)
              {
                //如果温度为开尔文则转化为摄氏度
                if(outHotStreamTUnit==="K")
                {
                  outHotStreamT = outHotStreamT-273.15;
                }
                if(inHotStreamTUnit==="K")
                {
                  inHotStreamT = inHotStreamT-273.15;
                }

                if(!(inHotStreamT>=outHotStreamT))
                {
                  errors.push({
                    msg: `${item.name}热流股${inHotStreamName}入口温度低于${outHotStreamName}出口温度`,
                    name: item.name,
                    type: 'element',
                    field: 't', 
                    fieldName: `热流股 ${inHotStreamName} 入口温度与 ${outHotStreamName} 出口温度`,
                    eleTypeName:item.type,
                  });
                }
              }
            }
          }
        }
      }
    }
  }

  if (errors.length > 0) {
    return errors;
  } else {
    return 'ok';
  }
}



// 验证函数
export const validateInput = (data: any): { elementsErrors: any, streamsErrors?: any } | 'ok' => {
  const { streams, elements } = data;
  const streamsErrors = verlidStrems(streams);
  const elementsErrors = verlidElements(elements,streams);
  if (streamsErrors === 'ok' && elementsErrors === 'ok') {
    return 'ok';
  } else {
    return { elementsErrors, streamsErrors };
  }
}
